import { createReducer } from "reduxsauce"
import actionCreator from "helpers/actionCreator"
import { get, find, filter, cloneDeep } from "lodash"
import { takeLatest, select, put, call } from "redux-saga/effects"
import { sendQuery } from "api/graphql"
import { LOADING, OK, ERROR } from "helpers/data-statuses"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Types as SmartTradeTypes } from "modules/smart-trade/model"
import { getModel } from "modules/research-topic-root/selectors"
import { getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { getSmartTradePositionSet } from "modules/smart-trade/selectors"
import { getContentSets } from "selectors/securities-table"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { getRawClassifications, getRuleDisplayType } from "./selectors"
import { sameByIdAndType, generateExposureQuery, generateSimulationQuery } from "./helpers"

export const { Types, Creators } = actionCreator("ompnt/modules/smartTradeRuleSearch", {
  setSmartTradeRule: ["rule"],
  removeSmartTradeRule: ["rule"],
  resetSmartTradeRules: [],
  currentSmartTradeValuesSuccess: ["data"],
  currentSmartTradeValuesLoading: [],
  currentSmartTradeValuesError: [],
})

const DEFAULT_STATE = {}

function handleRemoveSmartTradeRule(state, { rule }) {
  const rulesArray = state.smartTradeRules ? [...state.smartTradeRules] : []
  const newSmartTradeRules = filter(rulesArray, elem => !sameByIdAndType(elem, rule))
  return { ...state, smartTradeRules: newSmartTradeRules }
}

function handleResetSmartTradeRules(state) {
  return { ...state, smartTradeRules: [] }
}

function handleSetSmartTradeRule(state, { rule }) {
  const { min, max } = rule
  const newSmartTradeRules = state.smartTradeRules ? [...state.smartTradeRules] : []
  const item = find(newSmartTradeRules, elem => sameByIdAndType(elem, rule))

  if (item) {
    item.min = min
    item.max = max
  } else {
    newSmartTradeRules.push({ ...rule, min, max })
  }

  return { ...state, smartTradeRules: newSmartTradeRules }
}

function handleCurrentSmartTradeValuesLoading(state) {
  return { ...state, currentSmartTradeValuesStatus: LOADING }
}

function handleCurrentSmartTradeValuesError(state) {
  return {
    ...state,
    currentSmartTradeValuesStatus: ERROR,
    currentSmartTradeValues: null,
  }
}

function handleCurrentSmartTradeValuesSuccess(state, { data }) {
  return {
    ...state,
    currentSmartTradeValues: data,
    currentSmartTradeValuesStatus: OK,
  }
}

function handleLoadSmartTradeRules(state, { config: { setup: { smartTradeRules } } }) {
  return {
    ...state,
    smartTradeRules: cloneDeep(smartTradeRules),
  }
}

function* callCurrentSmartTradeValues() {
  const { account, modelId, contentSets, selectedDate, smartTradePositionSet, missingDataFiles, classifications } = yield select((state, props) => {
    return ({
      account: state.global.selected.account,
      modelId: getModel(state),
      selectedDate: getResearchTopicDate(state, props),
      smartTradePositionSet: getSmartTradePositionSet(state, props),
      contentSets: getContentSets(state, props),
      missingDataFiles: getMissingDataFiles(state),
      classifications: getRawClassifications(state, props),
      ruleDisplayType: getRuleDisplayType(state, props),
    })
  })
  yield put(Creators.currentSmartTradeValuesLoading())

  const query = `query ($modelId: String!, $positionSet: PositionSetInput!, $on: Date!) {
      model(id:$modelId) {
        simulation(positionSet: $positionSet, from: $on, to: $on){
          ${generateSimulationQuery(classifications)}
          ${generateExposureQuery(contentSets, missingDataFiles)}
        }
      }
    }`
  const variables = {
    modelId,
    positionSet: smartTradePositionSet,
    on: selectedDate,
  }

  const result = yield call(sendQuery, query, variables, account)
  const currentSmartTradeValuesError = get(result, "apiMessages.errors.0")

  if (currentSmartTradeValuesError) {
    yield put(notificationCreators.addApiNotification("error", null, currentSmartTradeValuesError.code, currentSmartTradeValuesError.message))
    yield put(Creators.currentSmartTradeValuesError())
  } else {
    const data = get(result, "data.model.simulation")
    yield put(Creators.currentSmartTradeValuesSuccess(data))
  }
}

export default createReducer(DEFAULT_STATE, {
  ...Creators,
  [Types.SET_SMART_TRADE_RULE]: handleSetSmartTradeRule,
  [Types.REMOVE_SMART_TRADE_RULE]: handleRemoveSmartTradeRule,
  [Types.RESET_SMART_TRADE_RULES]: handleResetSmartTradeRules,
  [Types.CURRENT_SMART_TRADE_VALUES_SUCCESS]: handleCurrentSmartTradeValuesSuccess,
  [Types.CURRENT_SMART_TRADE_VALUES_LOADING]: handleCurrentSmartTradeValuesLoading,
  [Types.CURRENT_SMART_TRADE_VALUES_ERROR]: handleCurrentSmartTradeValuesError,
  [SmartTradeTypes.LOAD_SMART_TRADE]: handleLoadSmartTradeRules,
})

export function* saga() {
  yield takeLatest([SmartTradeTypes.LOAD_SMART_TRADE, SmartTradeTypes.OPTIMIZATION_SUCCESS], callCurrentSmartTradeValues)
}
